
import React from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Button,
    Avatar,
    TextField,
    InputAdornment,
    Chip,
    Stack,
    useTheme,
    ThemeProvider
} from '@mui/material';
import {
    Search,
    Notifications,
    People,
    AttachMoney,
    AccessTime,
    Add,
    CheckCircle,
    Description
} from '@mui/icons-material';
import muiTheme from '../../theme/muiTheme';
import NewsWidget from './NewsWidget';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DashboardDemo = () => {
    const theme = useTheme();

    const handleQuickAction = (action) => {
        toast.info(`Clicked: ${action}`, {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
        });
    };

    return (
        <ThemeProvider theme={muiTheme}>
            <Box sx={{ flexGrow: 1, p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
                <ToastContainer />
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box>
                        <Typography variant="h4" sx={{ color: '#1e293b', mb: 1 }}>
                            Good Morning, Admin 👋
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Here's what's happening with your company today.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search..."
                            size="small"
                            sx={{
                                bgcolor: 'white',
                                borderRadius: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton sx={{ bgcolor: 'white', p: 1.5, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                            <Notifications color="action" />
                        </IconButton>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48, boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)' }}>AD</Avatar>
                    </Box>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {[
                        { title: 'Total Employees', value: '1,234', icon: <People />, color: '#667eea', increase: '+12%' },
                        { title: 'New Hires', value: '45', icon: <Add />, color: '#4fd1c5', increase: '+5%' },
                        { title: 'Total Payroll', value: '₹45.2L', icon: <AttachMoney />, color: '#f59e0b', increase: '+8%' },
                        { title: 'Avg. Attendance', value: '94%', icon: <AccessTime />, color: '#10b981', increase: '+2%' },
                    ].map((stat, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{
                                            p: 1.5,
                                            borderRadius: 3,
                                            bgcolor: `${stat.color}22`,
                                            color: stat.color,
                                            display: 'flex'
                                        }}>
                                            {stat.icon}
                                        </Box>
                                        <Chip
                                            label={stat.increase}
                                            size="small"
                                            sx={{
                                                bgcolor: '#ecfdf5',
                                                color: '#10b981',
                                                fontWeight: 600,
                                                borderRadius: 2
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="h4" sx={{ mb: 0.5, color: '#1e293b' }}>
                                        {stat.value}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                        {stat.title}
                                    </Typography>

                                    {/* Decorative background circle */}
                                    <Box sx={{
                                        position: 'absolute',
                                        right: -20,
                                        bottom: -20,
                                        width: 100,
                                        height: 100,
                                        borderRadius: '50%',
                                        bgcolor: `${stat.color}`,
                                        opacity: 0.05,
                                        pointerEvents: 'none'
                                    }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Main Content Grid */}
                <Grid container spacing={3}>
                    {/* Charts Section */}
                    <Grid item xs={12} lg={8}>
                        <NewsWidget />
                    </Grid>

                    {/* Quick Access & Recent Activity */}
                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            {/* Quick Access */}
                            <Card>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 3, color: '#1e293b' }}>Quick Access</Typography>
                                    <Grid container spacing={2}>
                                        {[
                                            { label: 'Add Employee', icon: <Add />, color: '#667eea' },
                                            { label: 'Run Payroll', icon: <AttachMoney />, color: '#4fd1c5' },
                                            { label: 'Time Off', icon: <AccessTime />, color: '#f59e0b' },
                                            { label: 'Documents', icon: <Description />, color: '#ef4444' },
                                        ].map((action, index) => (
                                            <Grid item xs={6} key={index}>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    onClick={() => handleQuickAction(action.label)}
                                                    sx={{
                                                        p: 2,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                        borderRadius: 3,
                                                        border: '1px solid #e2e8f0',
                                                        color: '#64748b',
                                                        '&:hover': {
                                                            borderColor: action.color,
                                                            color: action.color,
                                                            bgcolor: `${action.color}11`
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ color: action.color }}>{action.icon}</Box>
                                                    <Typography variant="caption" fontWeight={600}>{action.label}</Typography>
                                                </Button>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Recent Activity */}
                            <Card sx={{ flexGrow: 1 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 3, color: '#1e293b' }}>Recent Activity</Typography>
                                    <Stack spacing={2}>
                                        {[
                                            { text: 'New employee "Sarah Smith" onboarded', time: '2 hours ago', icon: <CheckCircle color="success" sx={{ fontSize: 20 }} /> },
                                            { text: 'Payroll for Oct 2023 approved', time: '5 hours ago', icon: <AttachMoney color="primary" sx={{ fontSize: 20 }} /> },
                                            { text: 'Leave request from "Mike" pending', time: '1 day ago', icon: <AccessTime color="warning" sx={{ fontSize: 20 }} /> },
                                        ].map((activity, index) => (
                                            <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                <Box sx={{ mt: 0.5 }}>{activity.icon}</Box>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500} sx={{ color: '#334155' }}>
                                                        {activity.text}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {activity.time}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </ThemeProvider>
    );
};

export default DashboardDemo;
